import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, MapPin, GraduationCap, 
  LogOut, Edit, Check, Upload,
  FileText, Clock, Trash2, Download, Eye, PenTool,
  LayoutDashboard, ChevronRight, Calendar, Loader2, X, 
  Receipt, FileDown, Sparkles, Plus, Heart, BookOpen,
  Video, MessageSquare, CheckCircle, XCircle, AlertCircle,
  Star, PhoneCall, RefreshCw, Badge
} from 'lucide-react';

import { useTheme } from '../Home/ThemeContext'; 
import { studentDashboardApi } from '../../services/studentDashboardApi'; 
import { formFillApi } from '../../services/formFillApi';
import { studentBookingApi } from '../../services/counselorApi2';
import { getCookie, deleteCookie } from '../../utils/auth';

// ─── Auth Guard: must be logged in as student ──────────────────────────────
function StudentAuthGuard({ children }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    const token = localStorage.getItem('token') || getCookie('auth_token');
    const userRaw = localStorage.getItem('user') || getCookie('user_data');
    
    if (!token) {
      navigate('/login?type=student&redirect=/student/dashboard');
      return;
    }

    try {
      const user = JSON.parse(decodeURIComponent(userRaw || '{}'));
      if (user.role && user.role !== 'student') {
        navigate('/login?type=student&message=Access+denied.+Please+login+as+a+student.');
      }
    } catch (e) {}
  }, [navigate]);

  return children;
}

// ─── Status Badge ──────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    draft:           'bg-gray-100 text-gray-600 border-gray-200',
    submitted:       'bg-blue-100 text-blue-700 border-blue-200',
    payment_pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    completed:       'bg-green-100 text-green-700 border-green-200',
    pending:         'bg-orange-100 text-orange-700 border-orange-200',
    confirmed:       'bg-teal-100 text-teal-700 border-teal-200',
    cancelled:       'bg-red-100 text-red-600 border-red-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${map[status] || map.draft}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

// ─── Counseling Booking Card ──────────────────────────────────────────────
function CounselingBookingCard({ booking, onCancel, isDarkMode }) {
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    if (!window.confirm('Cancel this counseling session?')) return;
    try {
      setCancelling(true);
      await onCancel(booking._id || booking.id);
    } finally {
      setCancelling(false);
    }
  };

  const statusIcon = {
    pending:         <Clock className="w-4 h-4 text-orange-500" />,
    confirmed:       <CheckCircle className="w-4 h-4 text-teal-500" />,
    payment_pending: <AlertCircle className="w-4 h-4 text-yellow-500" />,
    completed:       <CheckCircle className="w-4 h-4 text-green-500" />,
    cancelled:       <XCircle className="w-4 h-4 text-red-400" />,
  };

  return (
    <div className={`rounded-2xl border overflow-hidden transition-all hover:shadow-lg ${
      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-indigo-100 shadow-sm'
    }`}>
      {/* Top accent bar */}
      <div className={`h-1 w-full ${
        booking.status === 'confirmed' ? 'bg-gradient-to-r from-teal-400 to-green-500' :
        booking.status === 'cancelled' ? 'bg-red-400' :
        booking.status === 'completed' ? 'bg-green-500' :
        'bg-gradient-to-r from-indigo-400 to-purple-500'
      }`} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center border border-indigo-200">
              <User className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-gray-900">{booking.counselor_name || booking.counselorName || 'Counselor'}</h3>
              <p className="text-[11px] text-gray-400 mt-0.5">
                ID: {(booking._id || booking.id || '').toString().slice(-6).toUpperCase()}
              </p>
            </div>
          </div>
          <StatusBadge status={booking.status} />
        </div>

        {/* Details */}
        <div className="space-y-2.5 mb-4">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Calendar className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
            <span className="font-medium">{booking.preferred_slot || booking.preferredSlot || 'Slot TBD'}</span>
          </div>
          {booking.plan_name && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Badge className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
              <span>{booking.plan_name}</span>
            </div>
          )}
          {booking.amount && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="text-green-500 font-bold text-sm">₹</span>
              <span className="font-semibold text-gray-800">₹{booking.amount}</span>
              <span className="ml-auto flex items-center gap-1 text-gray-400">
                {statusIcon[booking.status]}
                <span className="capitalize">{booking.payment_status || 'N/A'}</span>
              </span>
            </div>
          )}
        </div>

        {/* Problem snippet */}
        {booking.problem_description && (
          <p className="text-[11px] text-gray-500 italic bg-gray-50 rounded-lg p-2 mb-4 line-clamp-2 border border-gray-100">
            "{booking.problem_description}"
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {booking.status === 'confirmed' && (
            <button className="flex-1 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 hover:opacity-90 transition">
              <Video className="w-3.5 h-3.5" /> Join Session
            </button>
          )}
          {(booking.status === 'pending' || booking.status === 'confirmed') && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="flex-1 py-2 border border-red-200 text-red-500 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-red-50 transition"
            >
              {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
              Cancel
            </button>
          )}
          {booking.status === 'completed' && (
            <div className="flex-1 py-2 bg-green-50 text-green-700 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 border border-green-200">
              <Star className="w-3.5 h-3.5 fill-green-500" /> Session Completed
            </div>
          )}
          {booking.status === 'cancelled' && (
            <div className="flex-1 py-2 bg-gray-50 text-gray-400 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 border border-gray-200">
              <XCircle className="w-3.5 h-3.5" /> Cancelled
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Application Card ──────────────────────────────────────────────────────
function ApplicationCard({ app, isDraft, isDarkMode, downloadingId, downloadingInvoiceId, downloadingBothId, onDownload, onDownloadInvoice, onDownloadBoth, navigate }) {
  const appId = app.id || app._id;
  const isPaid = app.status === 'submitted' || app.status === 'completed';
  const studentName = app.studentName || app.personalDetails?.fullName || 'Applicant';

  return (
    <div className={`p-4 rounded-xl border transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg text-white ${isDraft ? 'bg-orange-500' : 'bg-blue-600'}`}>
            {isDraft ? <PenTool size={18} /> : <GraduationCap size={18} />}
          </div>
          <div>
            <h3 className="font-bold text-sm truncate max-w-[140px]">{isDraft ? 'Draft Application' : studentName}</h3>
            <p className="text-[10px] text-gray-500 tracking-tight">ID: {appId?.slice(-6).toUpperCase()}</p>
          </div>
        </div>
        <StatusBadge status={app.status} />
      </div>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button
          onClick={() => navigate(isDraft ? '/student-form' : `/application-confirmation/${appId}`, { state: isDraft ? { draftData: app, isEditMode: true } : {} })}
          className={`py-2 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1 ${isDraft ? 'bg-orange-600 text-white' : 'bg-blue-600 text-white'}`}
        >
          {isDraft ? 'Continue' : <><Eye size={14}/> View</>}
        </button>
        {isPaid && (
          <button onClick={() => onDownloadBoth(appId)} disabled={downloadingBothId === appId}
            className="py-2 bg-purple-600 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-1">
            {downloadingBothId === appId ? <Loader2 size={14} className="animate-spin"/> : <><FileDown size={14}/> Bundle</>}
          </button>
        )}
      </div>
      {!isDraft && (
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => onDownload(appId)} disabled={downloadingId === appId} className="py-1.5 border border-gray-300 rounded text-[10px] font-bold flex items-center justify-center gap-1">
            {downloadingId === appId ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>} Form
          </button>
          <button onClick={() => onDownloadInvoice(appId)} disabled={downloadingInvoiceId === appId || !isPaid}
            className={`py-1.5 border rounded text-[10px] font-bold flex items-center justify-center gap-1 ${isPaid ? 'border-green-500 text-green-600' : 'opacity-30 cursor-not-allowed'}`}>
            {downloadingInvoiceId === appId ? <Loader2 size={12} className="animate-spin"/> : <Receipt size={12}/>} Invoice
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function EnhancedStudentDashboard() {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draftSelections, setDraftSelections] = useState([]);
  const [applications, setApplications] = useState([]);
  const [counselingBookings, setCounselingBookings] = useState([]);
  const [counselingLoading, setCounselingLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard'); 
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [downloadingBothId, setDownloadingBothId] = useState(null);
  const [editForm, setEditForm] = useState({ fullName: '', mobile: '', city: '', state: '', dob: '', gender: '', pincode: '' });
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // ── Auth check ──
  useEffect(() => {
    const token = localStorage.getItem('token') || getCookie('auth_token');
    if (!token) {
      navigate('/login?type=student&redirect=/student/dashboard');
      return;
    }
    loadUserData();
  }, [navigate]);

  useEffect(() => { if (user) refreshDashboardData(); }, [user]);
  useEffect(() => { if (activeTab === 'counseling') loadCounselingBookings(); }, [activeTab]);

  const loadUserData = () => {
    let currentUser = null;
    const localUser = localStorage.getItem('user');
    if (localUser) { try { currentUser = JSON.parse(localUser); } catch (e) {} }
    if (!currentUser) {
      const userData = getCookie('user_data');
      if (userData) { try { currentUser = JSON.parse(decodeURIComponent(userData)); } catch (e) {} }
    }
    if (currentUser) {
      setUser(currentUser);
      setEditForm({
        fullName: currentUser.fullName || '',
        mobile: currentUser.mobile || '',
        city: currentUser.city || '',
        state: currentUser.state || '',
        dob: currentUser.dob ? new Date(currentUser.dob).toISOString().split('T')[0] : '',
        gender: currentUser.gender || '',
        pincode: currentUser.pincode || '',
      });
    }
    setLoading(false);
  };

  const refreshDashboardData = async () => {
    try {
      const [appRes, draftRes] = await Promise.all([
        studentDashboardApi.getMyApplications(),
        studentDashboardApi.getDrafts(user?.id || user?._id),
      ]);
      if (appRes.success) setApplications(appRes.applications || []);
      if (draftRes.success) setDraftSelections(draftRes.drafts || []);
    } catch (err) { console.error('Dashboard refresh error:', err); }
  };

  const loadCounselingBookings = async () => {
    try {
      setCounselingLoading(true);
      const res = await studentBookingApi.getMyBookings();
      setCounselingBookings(res.bookings || []);
    } catch (err) {
      console.error('Failed to load counseling bookings:', err);
      setCounselingBookings([]);
    } finally {
      setCounselingLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await studentBookingApi.cancelBooking(bookingId);
      await loadCounselingBookings();
    } catch (err) {
      alert(err.message || 'Failed to cancel booking');
    }
  };

  const handleApplyNew = () => { localStorage.removeItem('pendingCollegeSelection'); navigate('/allcollege'); };

  const handleDownloadPDF = async (appId) => {
    try { setDownloadingId(appId); await formFillApi.downloadApplicationPDF(appId); }
    catch { alert('Failed to download PDF.'); }
    finally { setDownloadingId(null); }
  };

  const handleDownloadInvoice = async (appId) => {
    try { setDownloadingInvoiceId(appId); await formFillApi.downloadInvoicePDF(appId); }
    catch { alert('Failed to download Invoice.'); }
    finally { setDownloadingInvoiceId(null); }
  };

  const handleDownloadBoth = async (appId) => {
    try { setDownloadingBothId(appId); await formFillApi.downloadBothPDFs(appId); }
    catch { alert('Failed to download documents.'); }
    finally { setDownloadingBothId(null); }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Image size should be less than 2MB'); return; }
    setProfileImageFile(file);
    setProfileImagePreview(URL.createObjectURL(file));
  };

  const handleSaveProfile = async () => {
    try {
      setUploading(true);
      const formData = new FormData();
      Object.keys(editForm).forEach(key => { if (editForm[key]) formData.append(key, editForm[key]); });
      if (profileImageFile) formData.append('profileImage', profileImageFile);
      const response = await studentDashboardApi.updateProfile(user.id || user._id, formData);
      if (response.success || response.user) {
        const updatedUser = { ...user, ...response.user };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setEditing(false);
        setProfileImageFile(null);
        alert('✅ Profile updated successfully!');
      }
    } catch (err) {
      alert(err.message || '❌ Update failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleAction = async (action, id) => {
    try {
      if (action === 'delete_app') await studentDashboardApi.deleteApplication(id);
      if (action === 'delete_draft' || action === 'delete_list') await studentDashboardApi.deleteDraft(id);
      await refreshDashboardData();
    } catch { alert('Action failed'); }
  };

  const handleLogout = () => {
    deleteCookie('auth_token');
    deleteCookie('user_data');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login');
  };

  // ── Counseling stats ──
  const activeSessions = counselingBookings.filter(b => b.status === 'confirmed' || b.status === 'pending').length;
  const completedSessions = counselingBookings.filter(b => b.status === 'completed').length;

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="animate-spin w-10 h-10 text-blue-600"/>
    </div>
  );

  if (!user) return (
    <div className="h-screen flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-xl font-bold text-gray-700 mb-3">Session expired</h2>
        <button onClick={() => navigate('/login?type=student')} className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-bold">
          Login as Student
        </button>
      </div>
    </div>
  );

  const tabs = [
    { id: 'dashboard',  label: 'Dashboard',       icon: LayoutDashboard },
    { id: 'history',    label: 'Applications',    icon: FileText },
    { id: 'drafts',     label: 'Form Drafts',     icon: PenTool },
    { id: 'counseling', label: 'My Counseling',   icon: Video },
    { id: 'saved_list', label: 'Saved Colleges',  icon: Heart },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-gray-950 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>

      {/* ── Header ── */}
      <div className="bg-gradient-to-br from-blue-700 to-indigo-900 pt-24 pb-20 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="relative group">
              <div className="w-28 h-28 rounded-full border-4 border-white/20 overflow-hidden bg-white/10 backdrop-blur">
                {profileImagePreview || user.profileImage ? (
                  <img src={profileImagePreview || user.profileImage} alt="User" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-white">{user.fullName?.charAt(0)}</div>
                )}
              </div>
              {editing && (
                <label className="absolute bottom-0 right-0 bg-white text-blue-600 p-1.5 rounded-full cursor-pointer shadow-lg">
                  <Upload size={14} />
                  <input type="file" className="hidden" onChange={handleImageChange} accept="image/*" />
                </label>
              )}
            </div>
            <div className="text-center md:text-left text-white">
              <h1 className="text-3xl font-extrabold">{user.fullName}</h1>
              <p className="opacity-80 text-sm flex items-center justify-center md:justify-start gap-2 mt-1">
                <Mail size={14}/> {user.email}
              </p>
              {/* Counseling session quick stats in header */}
              {(activeSessions > 0 || completedSessions > 0) && (
                <div className="flex gap-3 mt-2">
                  {activeSessions > 0 && (
                    <span className="text-xs bg-white/20 border border-white/30 px-2.5 py-1 rounded-full font-semibold">
                      🎯 {activeSessions} Active Session{activeSessions > 1 ? 's' : ''}
                    </span>
                  )}
                  {completedSessions > 0 && (
                    <span className="text-xs bg-white/20 border border-white/30 px-2.5 py-1 rounded-full font-semibold">
                      ✅ {completedSessions} Completed
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={handleApplyNew} className="px-5 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-full font-bold text-sm shadow-xl flex items-center gap-2 transition-transform hover:scale-105">
              <Plus size={18} /> Apply New
            </button>
            <button onClick={() => navigate('/services/counselling')} className="px-5 py-2.5 bg-yellow-400 hover:bg-yellow-500 text-blue-900 rounded-full font-bold text-sm shadow-xl flex items-center gap-2">
              <Sparkles size={18} /> Book Counseling
            </button>
            <button onClick={() => setEditing(!editing)} className="px-5 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-white font-bold text-sm backdrop-blur">
              {editing ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-10">

        {/* ── Edit Profile Panel ── */}
        {editing && (
          <div className={`mb-8 p-6 rounded-2xl shadow-2xl border animate-in slide-in-from-top ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2 text-blue-600"><User size={20}/> Update Your Profile</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {['fullName', 'mobile', 'city', 'state', 'pincode'].map(f => (
                <div key={f} className="space-y-1">
                  <label className="text-[10px] font-bold uppercase text-gray-400 ml-1">{f}</label>
                  <input
                    value={editForm[f]}
                    onChange={e => setEditForm({ ...editForm, [f]: e.target.value })}
                    className={`w-full px-4 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500 outline-none ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <button onClick={handleSaveProfile} disabled={uploading} className="px-8 py-2.5 bg-blue-600 text-white rounded-lg font-bold flex items-center gap-2 disabled:opacity-50">
                {uploading ? <Loader2 className="animate-spin w-4 h-4"/> : <Check size={18}/>} Save Changes
              </button>
            </div>
          </div>
        )}

        {/* ── Tab Navigation ── */}
        <div className={`flex items-center gap-1 p-1 rounded-xl shadow-sm mb-8 overflow-x-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-[120px] py-3 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all ${
                activeTab === tab.id ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <tab.icon size={15}/> {tab.label}
              {tab.id === 'counseling' && activeSessions > 0 && (
                <span className="w-4 h-4 rounded-full bg-orange-500 text-white text-[9px] flex items-center justify-center font-black">
                  {activeSessions}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Content Area ── */}
        <div className="pb-20">

          {/* DASHBOARD TAB */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Applications', value: applications.filter(a => a.status !== 'draft').length, color: 'from-blue-500 to-blue-600', icon: FileText },
                  { label: 'Drafts', value: applications.filter(a => a.status === 'draft').length, color: 'from-orange-400 to-orange-500', icon: PenTool },
                  { label: 'Active Sessions', value: activeSessions, color: 'from-indigo-500 to-purple-600', icon: Video },
                  { label: 'Saved Colleges', value: draftSelections.length, color: 'from-pink-500 to-rose-500', icon: Heart },
                ].map(({ label, value, color, icon: Icon }) => (
                  <div key={label} className={`bg-gradient-to-br ${color} rounded-2xl p-5 text-white shadow-lg`}>
                    <Icon size={20} className="mb-2 opacity-80"/>
                    <div className="text-3xl font-black">{value}</div>
                    <div className="text-xs opacity-80 font-medium mt-0.5">{label}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Recent Applications */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold flex items-center gap-2"><Clock size={18}/> Recent Applications</h2>
                    <button onClick={() => setActiveTab('history')} className="text-blue-600 text-xs font-bold flex items-center gap-1 hover:underline">View All <ChevronRight size={14}/></button>
                  </div>
                  {applications.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {applications.slice(0, 4).map(app => (
                        <ApplicationCard key={app.id || app._id} app={app} isDraft={app.status === 'draft'} isDarkMode={isDarkMode}
                          downloadingId={downloadingId} downloadingInvoiceId={downloadingInvoiceId} downloadingBothId={downloadingBothId}
                          onDownload={handleDownloadPDF} onDownloadInvoice={handleDownloadInvoice} onDownloadBoth={handleDownloadBoth}
                          navigate={navigate}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="py-16 text-center border-2 border-dashed rounded-2xl border-gray-200">
                      <p className="text-gray-400 text-sm">No applications yet. Start your journey!</p>
                      <button onClick={handleApplyNew} className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold">Apply Now</button>
                    </div>
                  )}
                </div>

                {/* Sidebar */}
                <div className="space-y-5">
                  {/* Counseling Summary */}
                  <div className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                    <h3 className="font-bold text-sm mb-4 flex items-center gap-2 text-indigo-700">
                      <Video size={16} className="text-indigo-500"/> Counseling Sessions
                    </h3>
                    {counselingBookings.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-xs text-gray-400 mb-3">No sessions booked yet</p>
                        <button onClick={() => navigate('/services/counselling')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition w-full">
                          Book a Session
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {counselingBookings.slice(0, 3).map((b, i) => (
                          <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                            <div>
                              <p className="text-xs font-semibold text-gray-800 truncate max-w-[120px]">{b.counselor_name || 'Counselor'}</p>
                              <p className="text-[10px] text-gray-400">{b.preferred_slot?.slice(0, 20) || 'Slot TBD'}</p>
                            </div>
                            <StatusBadge status={b.status} />
                          </div>
                        ))}
                        {counselingBookings.length > 3 && (
                          <button onClick={() => setActiveTab('counseling')} className="w-full text-indigo-600 text-xs font-bold text-center pt-1 hover:underline">
                            View all {counselingBookings.length} sessions →
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Profile Strength */}
                  <div className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                    <h3 className="font-bold text-sm mb-3">Profile Strength</h3>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-1.5">
                      <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: user.city ? '100%' : '40%' }}></div>
                    </div>
                    <p className="text-[10px] text-gray-500">{user.city ? 'Complete profile' : 'Update city/state to complete'}</p>
                  </div>

                  <button onClick={handleLogout} className="w-full py-3 rounded-xl border-2 border-red-500/20 text-red-500 font-bold text-sm hover:bg-red-500/5 transition-colors flex items-center justify-center gap-2">
                    <LogOut size={18}/> Sign Out
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* APPLICATIONS TAB */}
          {activeTab === 'history' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {applications.filter(a => a.status !== 'draft').length === 0 ? (
                <div className="col-span-full py-20 text-center text-gray-400 border-2 border-dashed rounded-2xl">No submitted applications found.</div>
              ) : applications.filter(a => a.status !== 'draft').map(app => (
                <ApplicationCard key={app.id || app._id} app={app} isDarkMode={isDarkMode}
                  downloadingId={downloadingId} downloadingInvoiceId={downloadingInvoiceId} downloadingBothId={downloadingBothId}
                  onDownload={handleDownloadPDF} onDownloadInvoice={handleDownloadInvoice} onDownloadBoth={handleDownloadBoth}
                  navigate={navigate}
                />
              ))}
            </div>
          )}

          {/* DRAFTS TAB */}
          {activeTab === 'drafts' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {applications.filter(a => a.status === 'draft').length === 0 ? (
                <div className="col-span-full py-20 text-center text-gray-400 border-2 border-dashed rounded-2xl">No drafts found.</div>
              ) : applications.filter(a => a.status === 'draft').map(app => (
                <ApplicationCard key={app.id || app._id} app={app} isDraft isDarkMode={isDarkMode}
                  downloadingId={downloadingId} downloadingInvoiceId={downloadingInvoiceId} downloadingBothId={downloadingBothId}
                  onDownload={handleDownloadPDF} onDownloadInvoice={handleDownloadInvoice} onDownloadBoth={handleDownloadBoth}
                  navigate={navigate}
                />
              ))}
            </div>
          )}

          {/* COUNSELING TAB */}
          {activeTab === 'counseling' && (
            <div>
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Video size={20} className="text-indigo-600"/> My Counseling Sessions
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">{counselingBookings.length} session{counselingBookings.length !== 1 ? 's' : ''} total</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={loadCounselingBookings} disabled={counselingLoading} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-bold flex items-center gap-1.5 hover:bg-gray-50 transition">
                    <RefreshCw size={13} className={counselingLoading ? 'animate-spin' : ''}/> Refresh
                  </button>
                  <button onClick={() => navigate('/services/counselling')} className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-sm font-bold flex items-center gap-2 shadow-md hover:opacity-90 transition">
                    <Sparkles size={15}/> Book New Session
                  </button>
                </div>
              </div>

              {/* Status Summary */}
              {counselingBookings.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {[
                    { label: 'Pending',   value: counselingBookings.filter(b => b.status === 'pending').length,   color: 'bg-orange-50 border-orange-200 text-orange-700' },
                    { label: 'Confirmed', value: counselingBookings.filter(b => b.status === 'confirmed').length, color: 'bg-teal-50 border-teal-200 text-teal-700' },
                    { label: 'Completed', value: counselingBookings.filter(b => b.status === 'completed').length, color: 'bg-green-50 border-green-200 text-green-700' },
                    { label: 'Cancelled', value: counselingBookings.filter(b => b.status === 'cancelled').length, color: 'bg-red-50 border-red-200 text-red-600' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className={`${color} border rounded-xl p-3 text-center`}>
                      <div className="text-2xl font-black">{value}</div>
                      <div className="text-xs font-semibold mt-0.5">{label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Booking List */}
              {counselingLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="animate-spin w-10 h-10 text-indigo-600 mb-3"/>
                  <p className="text-gray-400 text-sm">Loading your sessions...</p>
                </div>
              ) : counselingBookings.length === 0 ? (
                <div className="py-20 text-center border-2 border-dashed rounded-2xl border-indigo-200 bg-indigo-50/30">
                  <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                    <Video className="w-8 h-8 text-indigo-500"/>
                  </div>
                  <h3 className="text-lg font-bold text-gray-700 mb-2">No Counseling Sessions Yet</h3>
                  <p className="text-gray-400 text-sm mb-6">Book a session with our expert counselors to get guidance on your academic and career journey.</p>
                  <button onClick={() => navigate('/services/counselling')} className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-bold shadow-lg hover:opacity-90 transition">
                    Browse Counselors
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {counselingBookings.map((booking, i) => (
                    <CounselingBookingCard key={booking._id || i} booking={booking} onCancel={handleCancelBooking} isDarkMode={isDarkMode} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SAVED COLLEGES TAB */}
          {activeTab === 'saved_list' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {draftSelections.length === 0 ? (
                <div className="col-span-full py-20 text-center text-gray-500 border-2 border-dashed rounded-2xl">No colleges saved in favorites yet.</div>
              ) : draftSelections.map((list, idx) => (
                <div key={idx} className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-pink-500">
                      <Heart size={18} fill="currentColor" />
                      <h3 className="font-bold text-sm">Favorite List</h3>
                    </div>
                    <button onClick={() => handleAction('delete_list', list._id)} className="text-gray-400 hover:text-red-500 transition">
                      <Trash2 size={16}/>
                    </button>
                  </div>
                  <div className="space-y-2 mb-6 min-h-[80px]">
                    {list.collegeDetails?.map((c, i) => (
                      <div key={i} className="text-xs flex items-center gap-2 text-gray-600">
                        <BookOpen size={12}/> {c.collegeName}
                      </div>
                    ))}
                  </div>
                  <button onClick={() => navigate('/allcollege', { state: { preSelectedColleges: list.collegeDetails, fromDraft: true } })}
                    className="w-full py-2 bg-blue-50 text-blue-600 rounded-lg text-xs font-bold hover:bg-blue-100 transition">
                    Apply with this List
                  </button>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}